import { Component, OnInit } from '@angular/core';
import { CoreService } from 'src/app/services/core.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppInfos, CLU_MOBILE_INTERFACE } from 'src/app/app.models';
import { AppService } from 'src/app/services/app.service';
import { AuthService } from 'src/app/@auth/auth.service';
import { AuthUtils } from 'src/app/utils/auth.utils';
import { MfaLoginService } from '../mfa-login/mfa-login.service';
import { RenewExpiredPasswordService } from '../renew-expired-password/renew-expired-password.service';
import { CluConfirmDialogService } from 'src/app/utils/clu-confirm-dialog/clu-confirm-dialog.service';
import { CluFeSnackbarService } from 'src/app/@clu-fe/apps/@services/clufe-snackbar.service';

const LS_FE_AUTH_CODE_SITE_KEY = 'LS_FE_AUTH_CODE_SITE_KEY';
const LS_FE_AUTH_USERNAME_KEY = 'LS_FE_AUTH_USERNAME_KEY';

@Component({
  selector: 'fe-login',
  templateUrl: './fe-login.component.html',
})
export class FELoginComponent implements OnInit {
  isTestEnv = false;
  options = this.settings.getOptions();
  loading = false;
  errorText: string | null = null;
  appVersion: AppInfos;
  form = new FormGroup({
    CodeSite: new FormControl('', [Validators.required]),
    Username: new FormControl('', [Validators.required]),
    Password: new FormControl('', [Validators.required]),
  });
  isPreprodEnv: boolean = ['development', 'preprod'].includes(this._appService.appVersion$.value.APP_ENVIRONMENT.toLocaleLowerCase());

  constructor(
    private settings: CoreService,
    private router: Router,
    private authService: AuthService,
    private mfaLoginService: MfaLoginService,
    private renewExpiredPasswordService: RenewExpiredPasswordService,
    private _confirmationService: CluConfirmDialogService,
    private _appService: AppService,
    private _snackbarService: CluFeSnackbarService
  ) {

    this.appVersion = _appService.appVersion$.value;

    const localCodeSite = localStorage.getItem(LS_FE_AUTH_CODE_SITE_KEY);
    if (!!localCodeSite) {
      this.f.CodeSite.patchValue(localCodeSite);
    }
    const localUsername = localStorage.getItem(LS_FE_AUTH_USERNAME_KEY);
    if (!!localUsername) {
      this.f.Username.patchValue(localUsername);
    }
    this.isTestEnv = this._appService.isFrontofficeTestEnv;
  }

  async ngOnInit() {
    await this.doDetectTestEnvironment();
  }

  private async doDetectTestEnvironment() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionTest = urlParams.get('sessionTest');
    if (sessionTest && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(sessionTest)) {
      await this.doSwitchToTestEnvironment(sessionTest);
    }
  }

  private async doSwitchToTestEnvironment(sessionTestId: string) {
      const confirmation = await this._confirmationService.showConfirmDialog("Mode Test", "Voulez-vous basculer vers le mode TEST ?");
      if (confirmation == true) {
        try {
            await this._appService.doSwitchFOToTestEnvironmentAction(sessionTestId);
            this._appService.doDeleteAllIndexedDbs();
            this._snackbarService.displayMessage("SUCCESS", "La bascule vers l'environnement de test a bien été effectuée. L'application va redémarrer.");
            location.href = "/";
        } catch (error: any) {
            this._snackbarService.displayMessage("ERROR", error);
        }
      }
  }

  async doSwitchToProdEnvironment() {
      const confirmation = await this._confirmationService.showConfirmDialog("Environnement de test", "Êtes-vous sûr de vouloir basculer vers l'environnement de production ? L'application va redémarrer.");
      if (confirmation == true) {
          this._appService.doDeleteAllIndexedDbs();
          localStorage.removeItem('LS_FE_SESSION_TEST_ENV');
          location.href = "/";
      }
  }

  get f() {
    return this.form.controls;
  }

  async submit() {
    if (this.form.invalid) { return; }

    this.errorText = null;
    this.loading = true;
    const codeSite = this.f.CodeSite.value!;
    const username = this.f.Username.value!;
    const password = this.f.Password.value!;

    this.form.disable();
    try {
      let token = await this.authService.doAuthenticateAction(codeSite, username, password);
      const claims = AuthUtils.parseJwt(token);
      if (claims.MFA_ENABLED == '1') {
        token = await this.mfaLoginService.show({ AccessToken: token, CluInterface: CLU_MOBILE_INTERFACE.FRONTOFFICE });
        if (!token) {
          throw "";
        }
      }

      const authSessionDto = await this.authService.doGetAuthSession(token);
      if (authSessionDto?.HAS_DEFAULT_PASSWORD === true) {
        const isPasswordRenewed = await this.renewExpiredPasswordService.show({ AccessToken: token });
        if (!isPasswordRenewed) {
          throw "Mot de passe non initialisé";
        }
      }

      localStorage.setItem(LS_FE_AUTH_CODE_SITE_KEY, codeSite);
      localStorage.setItem(LS_FE_AUTH_USERNAME_KEY, username);
      this.authService.saveAuthenticationSession(authSessionDto);
      await this.router.navigateByUrl('/apps');
      this.loading = false;
    } catch(e: any) {
      this.f.Password.patchValue('');
      this.errorText = e;
      this.loading = false;
      this.form.enable();
    }
  }
}
